import React, { useState, useEffect, useRef } from 'react';
import { searchFoods, getNutritionFromNaturalLanguage, getNutritionByItemId } from '../utils/nutritionixAPI';
import './FoodSearchAutocomplete.css';

function FoodSearchAutocomplete({ onFoodSelected, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ common: [], branded: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    // Focus input on mount
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Don't search if query is empty or too short
    if (query.length < 2) {
      setResults({ common: [], branded: [] });
      setShowResults(false);
      return;
    }

    // Debounce search - wait 300ms after user stops typing
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const searchResults = await searchFoods(query);
        setResults(searchResults);
        setShowResults(true);
      } catch (err) {
        console.error('Search error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query]);

  const handleSelectCommonFood = async (food) => {
    setLoading(true);
    setShowResults(false);
    setError(null);

    try {
      // Use natural language API to get full nutrition data
      const foodData = await getNutritionFromNaturalLanguage(food.food_name);

      if (foodData && foodData.length > 0) {
        onFoodSelected(foodData[0]);
      } else {
        throw new Error('No nutrition data found');
      }
    } catch (err) {
      console.error('Common food lookup error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSelectBrandedFood = async (food) => {
    setLoading(true);
    setShowResults(false);
    setError(null);

    try {
      // Use item lookup API to get full nutrition data
      const foodData = await getNutritionByItemId(food.nix_item_id);
      onFoodSelected(foodData);
    } catch (err) {
      console.error('Branded food lookup error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleNaturalLanguageSearch = async () => {
    if (query.trim().length === 0) return;

    setLoading(true);
    setShowResults(false);
    setError(null);

    try {
      const foodData = await getNutritionFromNaturalLanguage(query);

      if (foodData && foodData.length > 0) {
        // If multiple foods found, use the first one
        // Or you could show a selection UI for multiple results
        onFoodSelected(foodData[0]);
      } else {
        throw new Error('No nutrition data found for that query');
      }
    } catch (err) {
      console.error('Natural language search error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNaturalLanguageSearch();
    }
  };

  return (
    <div className="food-search-modal">
      <div className="search-container">
        <div className="search-header">
          <h3>🔍 Search Foods</h3>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="search-content">
          <div className="search-input-container">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search for food or enter meal (e.g., '2 eggs and bacon')"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            {query && (
              <button
                className="clear-button"
                onClick={() => {
                  setQuery('');
                  setShowResults(false);
                }}
              >
                ✕
              </button>
            )}
          </div>

          <button
            className="natural-language-button"
            onClick={handleNaturalLanguageSearch}
            disabled={!query || loading}
          >
            Get Nutrition for "{query || 'your meal'}"
          </button>

          {loading && (
            <div className="search-loading">
              <div className="spinner"></div>
              <p>Searching...</p>
            </div>
          )}

          {error && (
            <div className="search-error">
              <p>⚠️ {error}</p>
            </div>
          )}

          {showResults && !loading && (
            <div className="search-results">
              {results.common.length > 0 && (
                <div className="results-section">
                  <h4 className="results-section-title">Common Foods</h4>
                  <div className="results-list">
                    {results.common.map((food, index) => (
                      <div
                        key={`common-${index}`}
                        className="result-item"
                        onClick={() => handleSelectCommonFood(food)}
                      >
                        {food.photo && (
                          <img
                            src={food.photo.thumb}
                            alt={food.food_name}
                            className="result-photo"
                          />
                        )}
                        <div className="result-info">
                          <div className="result-name">{food.food_name}</div>
                          <div className="result-serving">
                            {food.serving_qty} {food.serving_unit}
                          </div>
                        </div>
                        <div className="result-arrow">→</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.branded.length > 0 && (
                <div className="results-section">
                  <h4 className="results-section-title">Branded Foods</h4>
                  <div className="results-list">
                    {results.branded.map((food, index) => (
                      <div
                        key={`branded-${index}`}
                        className="result-item"
                        onClick={() => handleSelectBrandedFood(food)}
                      >
                        {food.photo && (
                          <img
                            src={food.photo.thumb}
                            alt={food.food_name}
                            className="result-photo"
                          />
                        )}
                        <div className="result-info">
                          <div className="result-name">{food.food_name}</div>
                          <div className="result-brand">{food.brand_name}</div>
                          <div className="result-serving">
                            {food.serving_qty} {food.serving_unit}
                            {food.nf_calories && ` • ${Math.round(food.nf_calories)} cal`}
                          </div>
                        </div>
                        <div className="result-arrow">→</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.common.length === 0 && results.branded.length === 0 && (
                <div className="no-results">
                  <p>No results found for "{query}"</p>
                  <p className="no-results-hint">
                    Try using natural language instead: <br/>
                    "2 eggs and bacon" or "chicken breast"
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="search-tips">
          <p><strong>Search tips:</strong></p>
          <ul>
            <li><strong>Instant search:</strong> Type to see suggestions (e.g., "chicken", "apple")</li>
            <li><strong>Natural language:</strong> Describe your meal (e.g., "2 eggs and toast")</li>
            <li><strong>Brands:</strong> Search branded items (e.g., "Big Mac", "Cheerios")</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default FoodSearchAutocomplete;
